import * as React from 'react'
import NProgress from 'nprogress'
// libs
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router'

type UrlObject = {
  pathname: string
  query?: Record<string, string | string[]>
}

type Url = string | UrlObject

const resolveUrl = (url: Url): string => {
  if (typeof url === 'string') return url

  const { pathname, query } = url

  if (!query || Object.keys(query).length === 0) {
    return pathname
  }

  const searchParams = new URLSearchParams()

  Object.entries(query).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, v))
    } else if (value != null) {
      searchParams.set(key, value)
    }
  })

  const qs = searchParams.toString()

  return qs ? `${pathname}?${qs}` : pathname
}

export const useRouter = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()

  const [searchParams] = useSearchParams()

  return React.useMemo(() => {
    // Merge route params + search params into a single query object
    const query: Record<string, string | string[]> = { ...params }

    // Handle catch-all routes: React Router gives params['*'] as a string
    if ('*' in query) {
      const splatValue = query['*'] as string

      if (splatValue) {
        query.slug = splatValue.split('/')
      }
      delete query['*']
    }

    searchParams.forEach((value, key) => {
      query[key] = value
    })

    return {
      query,
      pathname: location.pathname,
      asPath: location.pathname + location.search,
      push: (url: Url, _as?: any, _options?: any) => {
        NProgress.start()
        navigate(resolveUrl(url))
      },
      replace: (url: Url, _as?: any, _options?: any) => {
        NProgress.start()
        navigate(resolveUrl(url), { replace: true })
      },
      back: () => {
        NProgress.start()
        navigate(-1)
      }
    }
  }, [navigate, location, params, searchParams])
}
